// sketchExtend — продление линии до пересечения с другой.
//
// Парный инструмент к «Обрезать»: там лишнее убирают, здесь недостающее
// дотягивают. Нужен, когда контур почти замкнут, но линии не достают друг
// до друга на пару миллиметров — выдавить такой эскиз нельзя, а двигать
// точки вручную долго и неточно.
//
// Как в Fusion: кликаешь по линии ближе к тому концу, который надо
// продлить, — он дотягивается до ближайшей линии на своём пути.

/** Пересечение двух прямых, заданных точкой и направлением. */
function rayLineIntersection(origin, dir, p1, p2) {
    const rx = dir.x;
    const ry = dir.y;
    const sx = p2.x - p1.x;
    const sy = p2.y - p1.y;
    const denom = rx * sy - ry * sx;
    // Прямые параллельны — пересечения нет.
    if (Math.abs(denom) < 1e-9) return null;
    const qpx = p1.x - origin.x;
    const qpy = p1.y - origin.y;
    // t — вдоль луча, u — вдоль отрезка-цели.
    const t = (qpx * sy - qpy * sx) / denom;
    const u = (qpx * ry - qpy * rx) / denom;
    return { t, u };
}

/**
 * Найти, куда продлить конец линии.
 *
 * line     — продлеваемая линия { p1, p2 };
 * fromEnd  — какой конец тянем: 'p1' или 'p2';
 * points   — Map id → { x, y };
 * others   — остальные линии эскиза.
 *
 * Возвращает { x, y } — новую позицию конца, или null.
 */
export function computeExtend(line, fromEnd, points, others) {
    const moving = points.get(fromEnd === 'p1' ? line.p1 : line.p2);
    const fixed = points.get(fromEnd === 'p1' ? line.p2 : line.p1);
    if (!moving || !fixed) return null;

    // Направление продления — от неподвижного конца к подвижному и дальше.
    const dx = moving.x - fixed.x;
    const dy = moving.y - fixed.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return null;
    const dir = { x: dx / len, y: dy / len };

    /*
     * Ищем ближайшее пересечение ВПЕРЁД по лучу.
     *
     * t > len — пересечение дальше текущего конца, то есть линию надо именно
     * продлить. Если бы брали t > 0, инструмент «дотягивал» бы конец назад,
     * к уже пройденному пересечению, — это обрезка, а не продление.
     */
    let best = null;
    for (const o of others) {
        if (o.id === line.id) continue;
        const a = points.get(o.p1);
        const b = points.get(o.p2);
        if (!a || !b) continue;
        const hit = rayLineIntersection(moving, dir, a, b);
        if (!hit) continue;
        // Пересечение должно попасть В ОТРЕЗОК цели, а не на её продолжение:
        // тянуть к воображаемому продолжению чужой линии бессмысленно.
        if (hit.u < -1e-6 || hit.u > 1 + 1e-6) continue;
        // И быть впереди по направлению продления.
        if (hit.t <= 1e-6) continue;
        if (!best || hit.t < best.t) best = hit;
    }
    if (!best) return null;

    return {
        x: moving.x + dir.x * best.t,
        y: moving.y + dir.y * best.t,
    };
}

/**
 * Какой конец линии ближе к точке клика — его и продлеваем.
 * Возвращает 'p1' или 'p2'.
 */
export function nearestEnd(line, points, x, y) {
    const a = points.get(line.p1);
    const b = points.get(line.p2);
    if (!a) return 'p2';
    if (!b) return 'p1';
    const da = (a.x - x) ** 2 + (a.y - y) ** 2;
    const db = (b.x - x) ** 2 + (b.y - y) ** 2;
    return da <= db ? 'p1' : 'p2';
}
